import type { Webhook } from '../../lib/flotiq-client/types/api-response';

import { useCallback, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { executeWebhooks } from '../../lib/flotiq-client';
import {
  parseResponseErrors,
  ResponseError,
} from '../../lib/flotiq-client/response-errors';
import { getTestProps } from '../../lib/helpers';

import useSelectedSpace from '../../hooks/useSelectedSpace';
import useToken from '../../hooks/useToken';

import { PlayCircleIcon } from '../../images/shapes';

import Button from '../Button/Button';
import Loader from '../Loader/Loader';
import Panel from '../Panel/Panel';

interface ContentObjectWebhooksProps {
  /**
   *  List of webhooks
   */
  webhooks: Webhook[];
  /**
   *  Content Object Id
   */
  contentObjectId?: string;
  /**
   *  Content Type Definition Name
   */
  contentTypeDefinitionName?: string;
  /**
   * Additional css classes
   */
  additionalClasses?: string;
  /**
   * Test id for information container
   */
  testId?: string;
}

const ContentObjectWebhooks = ({
  webhooks,
  contentObjectId,
  contentTypeDefinitionName,
  additionalClasses,
  testId = '',
}: ContentObjectWebhooksProps) => {
  const jwt = useToken();
  const { space } = useSelectedSpace();
  const { t } = useTranslation();
  const [loading, setLoading] = useState<string | undefined>();

  const handleExecuteWebhook = useCallback(
    async (id: string) => {
      setLoading(id);

      try {
        const { body, status, ok } = await executeWebhooks(jwt, space, {
          id,
          contentObjectId,
          contentTypeDefinitionName,
        });

        if (!ok) {
          throw parseResponseErrors(body, status);
        }

        toast.success(t('Webhooks.SendSuccess'));
      } catch (error) {
        if (!(error instanceof ResponseError)) {
          toast.error(t('Form.CommunicationErrorMessage'));
        } else {
          toast.error(error.message ? error.message : t('Webhooks.SendError'));
        }
      }
      setLoading(undefined);
    },
    [jwt, space, contentObjectId, contentTypeDefinitionName, t],
  );

  return (
    <Panel
      title={t('Webhooks.CustomWebhooks')}
      isCollapsable
      additionalTitleClasses="text-lg"
      additionalContainerClasses={twMerge('py-5 px-4', additionalClasses)}
      open
      {...getTestProps(testId, `webhooks-container`, 'testId')}
    >
      <div className="flex items-start flex-wrap gap-3">
        {webhooks.map((el) => (
          <Button
            key={el.id}
            onClick={() => handleExecuteWebhook(el.id)}
            iconImage={
              loading === el.id ? (
                <Loader type="spinner-grid" />
              ) : (
                <PlayCircleIcon className="w-5 text-blue group-hover:text-white dark:text-slate-200" />
              )
            }
            iconPosition="start"
            additionalClasses="w-max group max-w-full"
            additionalChildrenClasses={
              'leading-normal truncate whitespace-nowrap'
            }
            buttonColor="blueBordered"
            title={el.name}
            {...getTestProps(testId, `button-${el.id}`, 'testId')}
          >
            {el.name}
          </Button>
        ))}
      </div>
    </Panel>
  );
};

export default ContentObjectWebhooks;
